import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { gql, useQuery } from '@apollo/client'
import { sortByProperty } from 'helpers'
import { Set as ImmutableSet } from 'immutable'

import { Col, Row } from 'js/components/Grid/Grid'
import LoadingSpinner from 'js/components/LoadingSpinner/LoadingSpinner'
import SearchBar from 'js/components/SearchBar/SearchBar'
import Select from 'js/components/Select/Select'
import TagFields from 'js/components/TagFields/TagFields'
import useSortingOptions from 'js/page/Widget/hooks/useSortingOptions'

import InsertFromContentItems from './InsertFromContentItems'
import InsertFromContentSearchResults from './InsertFromContentSearchResults'

interface InsertFromContentProps {
    guid: string
    onInsert: (items: any[]) => void
    onClose: () => void
}

/**
 * This component is used in LinkList and Footer widget.
 */
function InsertFromContent({
    guid,
    onInsert,
    onClose,
}: InsertFromContentProps): JSX.Element {
    const { t } = useTranslation()
    const [typeFilter, setTypeFilter] = useState<
        string | string[] | undefined
    >()
    const [customTagFilter, setCustomTagFilter] =
        useState<ImmutableSet<any>>(ImmutableSet())
    const [categoryTagFilter, setCategoryTagFilter] = useState<any[]>([])
    const [orderBy, setOrderBy] = useState<string>('timePublished')
    const [searchInput, setSearchInput] = useState<string>('')
    const [searchInputSubmitted, setSearchInputSubmitted] = useState<string>('')
    const [orderDirection, setOrderDirection] = useState<string>('desc')
    const sortingOptions = useSortingOptions()

    const [items, setItems] = useState<any[]>([])

    const { loading, data } = useQuery(LINK_LIST_SEARCH_FILTERS_QUERY)

    const handleTypeFilter = (value: any) => {
        setTypeFilter(value)
    }

    const handleChangeSorting = (value: string) => {
        const [orderBy, orderDirection] = value.split('-')
        setOrderBy(orderBy)
        setOrderDirection(orderDirection)
    }

    const contentTypes =
        data?.site?.entityFilter?.contentTypes
            .map(({ key, value }) => ({ value: key, label: value }))
            .sort(sortByProperty('label')) || []

    const name = `widget-${guid}`

    const handleInsertItems = () => {
        onInsert(items)
        onClose()
    }

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(event.target.value)
    }

    const handleSearchSubmit = () => {
        setSearchInputSubmitted(searchInput)
    }

    if (loading || !data?.site) return <LoadingSpinner />

    return (
        <>
            <p>{t('widget-linklist.filters-helper')}</p>
            <Row $gutter={8} $spacing={12}>
                <Col mobileUp={1}>
                    <SearchBar
                        name="user-list-search"
                        label={t('form.search-by', {
                            attribute: t('form.title'),
                        })}
                        size="normal"
                        style={{ marginTop: '12px' }}
                        value={searchInput}
                        onChange={handleSearchChange}
                        onSearch={handleSearchSubmit}
                    />
                </Col>
            </Row>
            <Row $gutter={8} $spacing={12} style={{ marginTop: '16px' }}>
                <Col mobileUp={1 / 2}>
                    <Select
                        label={t('global.type')}
                        name={`${name}-typeFilter`}
                        placeholder={t('filters.filter-by')}
                        options={contentTypes}
                        value={typeFilter}
                        onChange={handleTypeFilter}
                        isMulti
                    />
                </Col>
                <Col mobileUp={1 / 2}>
                    <Select
                        label={t('filters.sorting')}
                        name={`widget-${guid}-sorting`}
                        options={sortingOptions}
                        value={`${orderBy}-${orderDirection}`}
                        onChange={handleChangeSorting}
                    />
                </Col>
            </Row>
            <Row $gutter={8} $spacing={12}>
                <Col mobileUp={1}>
                    {/* @ts-ignore FIXME: component is not typed */}
                    <TagFields
                        customTagsAllowed={data?.site?.customTagsAllowed}
                        customTags={customTagFilter}
                        setCustomTags={setCustomTagFilter}
                        allTagCategories={data?.site?.tagCategories}
                        style={{ marginTop: '12px' }}
                        categoryTags={categoryTagFilter}
                        setCategoryTags={setCategoryTagFilter}
                    />
                </Col>
            </Row>

            <Row $spacing={20}>
                <Col mobileLandscapeUp={1 / 2}>
                    <InsertFromContentSearchResults
                        customTags={customTagFilter.toJS()}
                        categoryTags={categoryTagFilter}
                        typeFilter={typeFilter}
                        orderBy={orderBy}
                        orderDirection={orderDirection}
                        searchInput={searchInputSubmitted}
                        items={items}
                        setItems={setItems}
                    />
                </Col>
                <Col mobileLandscapeUp={1 / 2}>
                    <InsertFromContentItems
                        items={items}
                        setItems={setItems}
                        onInsertLinks={handleInsertItems}
                    />
                </Col>
            </Row>
        </>
    )
}

const LINK_LIST_SEARCH_FILTERS_QUERY = gql`
    query LinkListSearchFilters {
        site {
            guid
            customTagsAllowed
            tagCategories {
                name
                values
            }
            entityFilter {
                contentTypes {
                    key
                    value
                }
            }
        }
    }
`
export default InsertFromContent
